import React, { useState } from 'react';
import { Mail, Phone, Send, CheckCircle, Github, Linkedin, Twitter, Instagram, Facebook, Youtube, Twitch, MessageCircle, Slack, Dribbble, Figma, Globe, MapPin, Calendar, Link, ExternalLink, Code, Coffee, Heart, Star, Bookmark } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { usePortfolio } from '../context/PortfolioContext';
import { portfolioAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

// Icon mapping - Discord mapped to MessageCircle since Discord icon doesn't exist in lucide-react
const iconMap = {
  Github, Linkedin, Twitter, Instagram, Facebook, Youtube, 
  Twitch, Discord: MessageCircle, Slack, Dribbble, Figma, Globe, 
  Mail, Phone, MapPin, Calendar, Link, ExternalLink,
  Code, Coffee, Heart, Star, Bookmark, Send
};

const Contact = () => {
  const { portfolioData, loading } = usePortfolio();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  if (loading || !portfolioData) {
    return null;
  }
  
  const { basics, socialLinks = [] } = portfolioData;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await portfolioAPI.submitContact(formData);
      if (response.success) {
        toast({
          title: "Message Sent!",
          description: response.message || "Thank you for reaching out. I'll get back to you soon.",
        });
        setFormData({ name: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-story-section className="pt-36 pb-32 bg-zinc-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,107,53,0.05),transparent_50%),radial-gradient(circle_at_70%_50%,rgba(251,146,60,0.05),transparent_50%)]"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-20 space-y-4">
          <h2 className="text-sm font-semibold text-orange-500 uppercase tracking-wider">Get In Touch</h2>
          <h3 className="text-5xl md:text-6xl font-bold text-white">
            Let's Work <span className="gradient-text">Together</span>
          </h3>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Have a project in mind or want to discuss opportunities? Feel free to reach out!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="glass p-8 rounded-3xl border border-orange-500/10 hover:border-orange-500/30 transition-all duration-300 group">
                <div className="flex items-start gap-6">
                  <div className="p-4 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                    <Mail size={28} className="text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-bold text-white text-lg mb-2">Email</h4>
                    <a
                      href={`mailto:${basics.email}`}
                      className="text-orange-500 hover:text-orange-400 transition-colors text-lg break-all"
                    >
                      {basics.email}
                    </a>
                  </div>
                </div>
              </div>

              <div className="glass p-8 rounded-3xl border border-orange-500/10 hover:border-orange-500/30 transition-all duration-300 group">
                <div className="flex items-start gap-6">
                  <div className="p-4 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                    <Phone size={28} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg mb-2">Phone</h4>
                    <a href={`tel:${basics.phone}`} className="text-orange-500 hover:text-orange-400 transition-colors text-lg">
                      {basics.phone}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative illustration placeholder */}
            <div className="hidden lg:block relative">
              <div className="glass w-full h-80 rounded-3xl border border-orange-500/10 flex items-center justify-center relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-amber-500/5"></div>
                <svg className="w-32 h-32 text-orange-500/30 group-hover:scale-110 transition-transform duration-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div className="absolute top-8 right-8 w-24 h-24 bg-orange-500/10 rounded-full blur-2xl"></div>
                <div className="absolute bottom-8 left-8 w-20 h-20 bg-amber-500/10 rounded-full blur-2xl"></div>
              </div>
            </div>

            {/* Social Links */}
            {socialLinks && socialLinks.length > 0 && (
              <div className="glass p-6 rounded-3xl border border-orange-500/10">
                <h4 className="font-bold text-white text-lg mb-4">Connect With Me</h4>
                <div className="flex flex-wrap gap-3">
                  {socialLinks
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((link, index) => {
                      const IconComponent = iconMap[link.icon] || Link;
                      return (
                        <a
                          key={link.id || index}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={link.tooltip || link.name}
                          className="group relative p-4 bg-zinc-900/50 rounded-xl border border-orange-500/10 hover:border-orange-500/50 hover:bg-orange-500/10 transition-all duration-300"
                        >
                          <IconComponent size={24} className="text-orange-500 group-hover:scale-110 transition-transform" />
                          {/* Tooltip */}
                          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-zinc-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-orange-500/20 pointer-events-none">
                            {link.tooltip || link.name}
                            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-900"></span>
                          </span>
                        </a>
                      );
                    })}
                </div>
              </div>
            )}
          </div>

          {/* Contact Form */}
          <div className="glass p-10 rounded-3xl border border-orange-500/10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-300 mb-3">
                  Name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-zinc-900 border-orange-500/20 text-white placeholder:text-gray-500 focus:border-orange-500 h-12"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-3">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-zinc-900 border-orange-500/20 text-white placeholder:text-gray-500 focus:border-orange-500 h-12"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-gray-300 mb-3">
                  Subject
                </label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  placeholder="What's this about?"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="bg-zinc-900 border-orange-500/20 text-white placeholder:text-gray-500 focus:border-orange-500 h-12"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-300 mb-3">
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Tell me about your project or inquiry..."
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="bg-zinc-900 border-orange-500/20 text-white placeholder:text-gray-500 focus:border-orange-500"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6 text-lg border-0 group"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
